import { Box, Button, ClickAwayListener, FormLabel, Grid, Paper, Popover } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import chroma from "chroma-js";
import { FieldMetaProps } from "formik";
import React, { useCallback, useMemo, useState } from "react";
import { ColorChangeHandler, ColorResult, GithubPicker } from "react-color";
import { FieldError } from "./FieldError";
import { OCFormControl } from "./FormControl";
export interface IColorPickFieldProps {
  className?: string;
  label: string;
  name: string;
  value?: string;
  onChange?: (e: string, event: React.ChangeEvent<HTMLInputElement>) => void;
  meta?: Partial<FieldMetaProps<any>>;
  disabled?: boolean;
  labelSX?: any;
  /** Override the default color palette shown in the picker. */
  colors?: string[];
  /** Override the picker popover width (default "213px"). */
  pickerWidth?: string;
}

// COLOR PICK COMPONENT
export const ColorPickField = (props: IColorPickFieldProps): JSX.Element => {
  const { label, name, onChange, value, className, disabled, meta, labelSX, colors: customColors, pickerWidth } = props;
  const theme = useTheme();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<Element>(null);

  // Track if formik value is dirty.
  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { initialValue, touched } = meta;
    // Temp fix as field seems to be getting defined as null when undefined is passed in as initial value
    const valueNullOrUndefined =
      (initialValue === null && value === undefined) || (initialValue === undefined && value === null);
    return touched && value !== initialValue && !valueNullOrUndefined ? true : false;
  }, [meta, value]);

  const handleClick = (e: any): void => {
    e.preventDefault();
    setIsFocused(!isFocused);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (): void => {
    setIsFocused(false);
    setAnchorEl(null);
  };

  const handleChangeColor: ColorChangeHandler = useCallback(
    (color: ColorResult, event: React.ChangeEvent<HTMLInputElement>): void => {
      onChange(color?.hex ?? theme.custom.timeline.groupColumnDivider, event);
      handleClose();
    },
    [onChange, theme.custom.timeline.groupColumnDivider]
  );

  const handleClearColor = useCallback(
    (event: any): void => {
      onChange(null, event);
      handleClose();
    },
    [onChange]
  );

  const hasError = meta?.error ? true : false;
  return (
    <OCFormControl error={hasError} sx={{ width: "100%", my: 0.3 }}>
      <Grid container sx={{ display: "flex", alignItems: "start" }}>
        <Grid
          item
          sx={{
            display: "flex",
            justifyContent: hasError ? "space-between" : "flex-end",
          }}
          xs
        >
          <FieldError meta={meta} icon={true} />
          {label && <FormLabel sx={{ ...labelSX }}>{label}</FormLabel>}
        </Grid>
        <Grid item xs={7}>
          <Box
            className="input-base"
            onClick={handleClick}
            sx={{
              border: `2px solid ${chroma(value ? value : theme.custom.forms.labels)
                .darken(2)
                .css()}`,
              borderRadius: 1,
              height: "24px",
              background: value ? value : "repeating-linear-gradient(45deg, #888, #888 3px, #BCBCBC 3px, #BCBCBC 6px)",
              width: "48px",
              marginLeft: "5px",
              cursor: "pointer",
              "&:focus, &:hover": {
                boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
                borderColor: theme.palette.primary.main,
              },
            }}
            id={"id-" + name}
          >
            {" "}
          </Box>
          {isFocused && (
            <ClickAwayListener onClickAway={() => handleClose()}>
              <Popover
                anchorEl={anchorEl}
                anchorReference="anchorEl"
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                open={!!anchorEl}
                onClose={() => handleClose()}
              >
                <Paper
                  elevation={8}
                  sx={{
                    p: 1,
                    background: "#000",
                  }}
                >
                  <Grid container columns={6} spacing={3} alignItems="center">
                    <Grid item xs>
                      <GithubPicker
                        color={value ? value : ""}
                        {...props}
                        onChange={handleChangeColor}
                        width={pickerWidth ?? "213px"}
                        triangle={"hide"}
                        colors={
                          customColors ?? [
                            theme.custom.timeline.groupColumnDivider,
                            "#29bf12",
                            "#2364aa",
                            "#08bdbd",
                            "#f21b3f",
                            "#ff9914",
                            "#FF88DC",
                            "#CAE5FF",
                          ]
                        }
                      />
                    </Grid>
                    <Grid item xs="auto">
                      <Button onClick={handleClearColor} size="small" variant="contained" color="info">
                        Clear
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Popover>
            </ClickAwayListener>
          )}
        </Grid>
      </Grid>
    </OCFormControl>
  );
};
